/*eslint @typescript-eslint/ban-types: "off" */
import React, { useState } from 'react';
import RolesListItem from './RolesListItem';
import { RolesListContainer } from './styles';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { ICompanyRole, TProjects } from 'store/companies/companiesTypes';
import { IUsers } from 'store/store';
import { ITags } from 'store/providers/providerTypes';
import cloneDeep from 'lodash/cloneDeep';

interface IRolesListProps {
  roles: ICompanyRole[];
  allRoles: ICompanyRole[];
  deleteRole: (roleId: number, companyId: number) => () => void;
  updateRole: (role: ICompanyRole) => Promise<any>;
  addPrefixRoles: (role: ICompanyRole) => Promise<any>;
  allProjectTags: string[];
  users: IUsers;
  projects: TProjects;
  enableReOrder: boolean;
  tags: ITags;
  providerIds: number[];
  isAdvancedEditor: boolean;
  isAdvancedAdmin: boolean;
}

const RolesList: React.FC<IRolesListProps> = ({
  roles,
  deleteRole,
  updateRole,
  addPrefixRoles,
  allProjectTags,
  projects,
  users,
  allRoles,
  enableReOrder,
  tags,
  providerIds,
  isAdvancedEditor,
  isAdvancedAdmin,
}) => {
  const [tempOrderOverride, setTempOrderOverride] = useState({});
  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const newIndex = result.destination.index;
    const oldIndex = result.source.index;
    // when order not changed do nothing
    if (newIndex === oldIndex) {
      return;
    }
    let newOrder;
    const getOrder = (role: ICompanyRole, tempOrderOverride: {}) => {
      return tempOrderOverride[role.id] || role.order || 0;
    };
    const sortedRoles = roles.sort(
      (a: ICompanyRole, b: ICompanyRole) =>
        getOrder(a, tempOrderOverride) - getOrder(b, tempOrderOverride)
    );
    const item = sortedRoles[oldIndex];

    if (newIndex === 0) {
      // dropped in first of list
      if (sortedRoles.length === 0) {
        // dropped in empty list
        newOrder = 0;
      } else {
        const bottomItemOrder = getOrder(
          sortedRoles[newIndex],
          tempOrderOverride
        );
        newOrder = bottomItemOrder - 1;
      }
    } else if (newIndex + 1 === sortedRoles.length) {
      // dropped in end of list
      const topItemOrder = getOrder(
        sortedRoles[newIndex - 1],
        tempOrderOverride
      );
      newOrder = topItemOrder + 1;
    } else if (newIndex > oldIndex) {
      // pull down a list itemSortedQuestion
      const topItemOrder = getOrder(sortedRoles[newIndex], tempOrderOverride);
      const bottomItemOrder = getOrder(
        sortedRoles[newIndex + 1],
        tempOrderOverride
      );
      newOrder = (topItemOrder + bottomItemOrder) / 2;
    } else {
      // pull up a list item
      const topItemOrder = getOrder(
        sortedRoles[newIndex - 1],
        tempOrderOverride
      );
      const bottomItemOrder = getOrder(
        sortedRoles[newIndex],
        tempOrderOverride
      );
      newOrder = (topItemOrder + bottomItemOrder) / 2;
    }
    const role: ICompanyRole = cloneDeep(item);
    role.order = newOrder;
    setTempOrderOverride({ ...tempOrderOverride, [role.id]: newOrder });

    const clearOrder = () => {
      if (tempOrderOverride[role.id] === role.order)
        setTempOrderOverride({ ...tempOrderOverride, [role.id]: null });
    };
    // update Task
    updateRole(role).then(
      () => {
        clearOrder();
      },
      () => {
        clearOrder();
      }
    );
  };

  const list = roles
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((role, index) => {
      return role ? (
        enableReOrder ? (
          <Draggable draggableId={role.id.toString()} index={index}>
            {(provided) => {
              return (
                <RolesListItem
                  provided={provided}
                  key={index}
                  roleItem={role}
                  allRoles={allRoles}
                  deleteRole={deleteRole}
                  updateRole={updateRole}
                  addPrefixRoles={addPrefixRoles}
                  allProjectTags={allProjectTags}
                  projects={projects}
                  providerIds={providerIds}
                  users={users}
                  tags={tags}
                  isAdvancedEditor={isAdvancedEditor}
                  isAdvancedAdmin={isAdvancedAdmin}
                />
              );
            }}
          </Draggable>
        ) : (
          <RolesListItem
            key={index}
            roleItem={role}
            allRoles={allRoles}
            deleteRole={deleteRole}
            updateRole={updateRole}
            addPrefixRoles={addPrefixRoles}
            allProjectTags={allProjectTags}
            projects={projects}
            providerIds={providerIds}
            users={users}
            tags={tags}
            isAdvancedEditor={isAdvancedEditor}
            isAdvancedAdmin={isAdvancedAdmin}
          />
        )
      ) : (
        <p>not support</p>
      );
    });

  return enableReOrder ? (
    // @ts-expect-error JSX error
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="droppable-1">
        {(provided) => (
          <RolesListContainer
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {list}
          </RolesListContainer>
        )}
      </Droppable>
    </DragDropContext>
  ) : (
    <RolesListContainer>{list}</RolesListContainer>
  );
};

export default RolesList;
